import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { getStates, type State } from "@/lib/api/statesService";
import { makeJobPreviousOrCurrent } from "@/lib/api/applicationService";
import { showError, showSuccess } from "@/lib/utils/notifications";
import { calculatePropertyToEmployerDistance } from "@/lib/utils/distanceMatrix";
import { translateFieldOnBlur } from "@/lib/utils/translateFieldOnBlur";
import {
  validateIncomeEmploymentTab,
  deriveJobDurationMonths,
  mergePreviousJobGaps,
  type OtherSourceInput,
  type OtherSourceValidatedField,
} from "../../../../utils/incomeEmploymentValidator";
import type { EmploymentFormRow } from "../../../../types/tabs";
import {
  enrichOtherIncomeRow,
  buildPropertyAddressForDistance,
  buildEmployerAddressForDistance,
  parseJobDistance,
  isEndDateBeforeStartDate,
} from "../incomeEmploymentHelpers";
import {
  extractEmploymentJobsFromApplication,
  buildEmptyEmploymentRow,
  buildEmploymentRowFromJob,
  buildOtherIncomeFormsFromApi,
  isEmploymentRowMeaningful,
  buildEmploymentApiPayload,
  patchEmploymentRow,
  patchEmploymentRowFields,
  isPreviousEmployerActiveOnApplication,
} from "../employmentFormModel";
import type { OtherIncomeFormRow } from "../../../../types/incomeEmploymentApi";
import { employmentEnterDateKey } from "../../../../pages/applicationDetailHelpers";

function parseSavedJobId(
  jobId: string | number | undefined | null,
): string | number | null {
  const raw = String(jobId ?? "").trim();
  if (!raw) return null;
  const asNumber = Number(raw);
  return Number.isFinite(asNumber) ? asNumber : raw;
}

function appendDeletedJobId(
  ids: Array<number | string>,
  jobId: string | number | undefined | null,
): Array<number | string> {
  const parsed = parseSavedJobId(jobId);
  if (parsed == null) return ids;
  if (ids.some((id) => String(id) === String(parsed))) return ids;
  return [...ids, parsed];
}

function appendDeletedJobIdsFromRows(
  ids: Array<number | string>,
  rows: Array<{ job_id?: string | number }>,
): Array<number | string> {
  return rows.reduce(
    (acc, row) => appendDeletedJobId(acc, row.job_id),
    ids,
  );
}

export function useEmploymentFormState(options: {
  applicationData: Record<string, unknown>;
  applicationAllData?: Record<string, unknown>;
  applicantId?: string;
  isEdit: boolean;
  onFormChange?: (formData: Record<string, unknown>) => void;
  onRefresh?: () => void | Promise<void>;
  exposeIncomeInlineValidation: boolean;
}) {
  const {
    applicationData,
    applicationAllData,
    applicantId,
    isEdit,
    onFormChange,
    onRefresh,
    exposeIncomeInlineValidation,
  } = options;

  const { currentJobs, previousJobs, otherIncomeData } =
    extractEmploymentJobsFromApplication(
      applicationData as Parameters<typeof extractEmploymentJobsFromApplication>[0],
    );

  const [otherIncomeForms, setOtherIncomeForms] = useState<OtherIncomeFormRow[]>(
    () => buildOtherIncomeFormsFromApi(otherIncomeData),
  );
  const [otherIncomeTouched, setOtherIncomeTouched] = useState<
    Record<string, boolean>
  >({});

  const [currentJobForms, setCurrentJobForms] = useState<EmploymentFormRow[]>(
    () =>
      currentJobs.length > 0
        ? currentJobs.map(buildEmploymentRowFromJob)
        : [buildEmptyEmploymentRow()],
  );
  const [previousJobForms, setPreviousJobForms] = useState<EmploymentFormRow[]>(
    () =>
      isPreviousEmployerActiveOnApplication(applicationData, previousJobs.length)
        ? previousJobs.map(buildEmploymentRowFromJob)
        : [],
  );

  const [employmentStatus, setEmploymentStatus] = useState<string>(
    applicationData?.employment_status === "1" ||
      applicationData?.employment_status === "0"
      ? String(applicationData.employment_status)
      : "",
  );
  const [hasPreviousEmployer, setHasPreviousEmployer] = useState(() =>
    isPreviousEmployerActiveOnApplication(applicationData, previousJobs.length),
  );
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [statesList, setStatesList] = useState<State[]>([]);

  const [pendingMakePreviousIndex, setPendingMakePreviousIndex] = useState<
    number | null
  >(null);
  const [pendingMakePreviousEndDate, setPendingMakePreviousEndDate] =
    useState("");
  const [makePreviousConfirm, setMakePreviousConfirm] = useState<{
    rowIndex: number;
    endDate: string;
  } | null>(null);
  const [makeCurrentConfirm, setMakeCurrentConfirm] = useState<{
    rowIndex: number;
  } | null>(null);
  /** Angular `deleted_data.resident_job` — cleared after save + refetch re-seeds jobs. */
  const [deletedJobIds, setDeletedJobIds] = useState<Array<number | string>>([]);
  /** Angular `entered_date` — employment date field entry timestamps. */
  const [enterDate, setEnterDate] = useState<Record<string, string>>({});
  const deletedJobIdsRef = useRef(deletedJobIds);
  deletedJobIdsRef.current = deletedJobIds;
  const enterDateRef = useRef(enterDate);
  enterDateRef.current = enterDate;

  const emitFormChange = useCallback(
    (
      nextCurrent: EmploymentFormRow[],
      nextPrevious: EmploymentFormRow[],
      nextOther: OtherIncomeFormRow[] = otherIncomeForms,
      employmentStatusOverride?: string,
      deletedOverride?: Array<number | string>,
      enterDateOverride?: Record<string, string>,
    ) => {
      const payload = buildEmploymentApiPayload({
        currentRows: nextCurrent,
        previousRows: nextPrevious,
        otherRows: nextOther,
        employmentStatus,
        applicationData: applicationData as {
          underwriter_verified_income_other?: unknown;
          underwriter_verified_income_type?: unknown;
        },
        employmentStatusOverride,
      });
      const nextDeleted = deletedOverride ?? deletedJobIdsRef.current;
      const nextEnterDate = enterDateOverride ?? enterDateRef.current;
      if (nextDeleted.length > 0) {
        payload.deleted = { resident_job: nextDeleted };
      }
      if (Object.keys(nextEnterDate).length > 0) {
        payload.enter_date = nextEnterDate;
      }
      onFormChange?.(payload);
    },
    [applicationData, employmentStatus, onFormChange, otherIncomeForms],
  );


  const buildEnterDatePatch = (
    field: "job_start_date" | "job_end_date",
    section: "current_employment" | "previous_employment" | "other_source",
    index: number,
    value: string | number | boolean,
  ): Record<string, string> | undefined => {
    const text = String(value ?? "").trim();
    if (!text) return undefined;
    const key = employmentEnterDateKey(field, section, index);
    return { ...enterDateRef.current, [key]: text };
  };

  const commitEmploymentForms = useCallback(
    (
      nextCurrent: EmploymentFormRow[],
      nextPrevious: EmploymentFormRow[],
      employmentStatusOverride?: string,
      extras?: {
        deletedOverride?: Array<number | string>;
        enterDateOverride?: Record<string, string>;
      },
    ) => {
      const previousWithGaps = mergePreviousJobGaps(nextCurrent, nextPrevious);
      setCurrentJobForms(nextCurrent);
      setPreviousJobForms(previousWithGaps);
      if (extras?.enterDateOverride) {
        setEnterDate(extras.enterDateOverride);
      }
      emitFormChange(
        nextCurrent,
        previousWithGaps,
        otherIncomeForms,
        employmentStatusOverride,
        extras?.deletedOverride,
        extras?.enterDateOverride,
      );
    },
    [emitFormChange, otherIncomeForms],
  );

  const employmentSeedKey = useMemo(() => {
    const { currentJobs, previousJobs } = extractEmploymentJobsFromApplication(
      applicationData as Parameters<typeof extractEmploymentJobsFromApplication>[0],
    );
    return JSON.stringify({
      employment_status: applicationData?.employment_status,
      previous_employer: applicationData?.previous_employer,
      currentJobs,
      previousJobs,
    });
  }, [
    applicationData,
    applicationData?.employment_status,
    applicationData?.previous_employer,
    applicationData?.current_job,
    applicationData?.current_employment,
    applicationData?.previous_job,
    applicationData?.previous_employment,
  ]);

  useEffect(() => {
    if (!isEdit) {
      setCurrentJobForms([buildEmptyEmploymentRow()]);
      setPreviousJobForms([]);
      setOtherIncomeForms([]);
      setHasPreviousEmployer(false);
      return;
    }

    const extracted = extractEmploymentJobsFromApplication(
      applicationData as Parameters<typeof extractEmploymentJobsFromApplication>[0],
    );
    const { currentJobs, previousJobs, otherIncomeData } = extracted;

    const nextCurrent =
      currentJobs.length > 0
        ? currentJobs.map(buildEmploymentRowFromJob)
        : [buildEmptyEmploymentRow()];
    const nextPrevious = mergePreviousJobGaps(
      nextCurrent,
      previousJobs.map(buildEmploymentRowFromJob),
    );
    const nextOtherForms = buildOtherIncomeFormsFromApi(otherIncomeData);
    const showPrevious = isPreviousEmployerActiveOnApplication(
      applicationData,
      previousJobs.length,
    );
    const nextPreviousForms = showPrevious ? nextPrevious : [];

    const statusRaw = applicationData?.employment_status;

    setCurrentJobForms(nextCurrent);
    setPreviousJobForms(nextPreviousForms);
    setOtherIncomeForms(nextOtherForms);
    setOtherIncomeTouched({});
    setDeletedJobIds([]);
    setEnterDate({});
    setHasPreviousEmployer(showPrevious);
    if (statusRaw === "1" || statusRaw === "0") {
      setEmploymentStatus(String(statusRaw));
    }

    emitFormChange(
      nextCurrent,
      nextPreviousForms,
      nextOtherForms,
      statusRaw === "1" || statusRaw === "0" ? String(statusRaw) : undefined,
      [],
      {},
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, applicationData?.applicant_id, employmentSeedKey]);

  useEffect(() => {
    setSubmitAttempted(false);
  }, [applicationData?.applicant_id, isEdit]);

  useEffect(() => {
    const onValidate = () => setSubmitAttempted(true);
    window.addEventListener("liberty:income-employment:validate", onValidate);
    return () =>
      window.removeEventListener("liberty:income-employment:validate", onValidate);
  }, []);

  useEffect(() => {
    const raw = applicationData?.employment_status;
    if (raw === "1" || raw === "0") {
      setEmploymentStatus(String(raw));
    } else if (raw === undefined || raw === null || raw === "") {
      setEmploymentStatus("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationData]);

  useEffect(() => {
    let cancelled = false;
    getStates()
      .then((list) => {
        if (cancelled) return;
        const valid = list.filter(
          (s) =>
            String(s.state_code ?? "").trim() !== "" &&
            String(s.state_name ?? "").trim() !== "",
        );
        valid.sort((a, b) =>
          a.state_name.localeCompare(b.state_name, undefined, {
            sensitivity: "base",
          }),
        );
        setStatesList(valid);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setStatesList([]);
          showError(
            err instanceof Error
              ? err.message
              : "Could not load states. Please refresh or try again later.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleOtherIncomeChange = (
    index: number,
    field: string,
    value: string | number | boolean,
  ) => {
    const newForms = [...otherIncomeForms];
    const updatedRow = { ...newForms[index], [field]: value };
    if (field === "present_job" && value) {
      updatedRow.job_end_date = "";
    }
    if (field === "job_end_date" && value) {
      updatedRow.present_job = false;
    }
    const nextRow = enrichOtherIncomeRow(updatedRow);
    if (
      (field === "job_start_date" || field === "job_end_date") &&
      isEndDateBeforeStartDate(nextRow.job_start_date, nextRow.job_end_date)
    ) {
      showError("Job End Date should be after Job Start Date");
      nextRow.job_end_date = "";
      nextRow.job_duration = "";
    }
    newForms[index] = nextRow as OtherIncomeFormRow;
    setOtherIncomeForms(newForms);
    setOtherIncomeTouched((prev) => ({
      ...prev,
      [`${index}.${field}`]: true,
    }));
    if (field === "job_start_date" || field === "job_end_date") {
      const nextEnterDate = buildEnterDatePatch(
        field,
        "other_source",
        index,
        value,
      );
      if (nextEnterDate) {
        setEnterDate(nextEnterDate);
      }
      emitFormChange(
        currentJobForms,
        previousJobForms,
        newForms,
        undefined,
        deletedJobIdsRef.current,
        nextEnterDate,
      );
      return;
    }
    emitFormChange(
      currentJobForms,
      previousJobForms,
      newForms,
    );
  };

  const handleAddOtherIncome = () => {
    const newForms: OtherIncomeFormRow[] = [
      ...otherIncomeForms,
      {
        job_id: "",
        income_period: "Per_Month",
        source_name: "",
        income: "",
        job_start_date: "",
        job_end_date: "",
        present_job: false,
        job_duration: "",
      },
    ];
    setOtherIncomeForms(newForms);
    emitFormChange(currentJobForms, previousJobForms, newForms);
  };

  const handleRemoveOtherIncome = (index: number) => {
    const removed = otherIncomeForms[index];
    const nextDeleted = appendDeletedJobId(
      deletedJobIdsRef.current,
      removed?.job_id,
    );
    setDeletedJobIds(nextDeleted);
    const newForms = otherIncomeForms.filter((_, i) => i !== index);
    setOtherIncomeForms(newForms);
    emitFormChange(
      currentJobForms,
      previousJobForms,
      newForms,
      undefined,
      nextDeleted,
    );
  };

  const handleCurrentJobChange = (
    rowIndex: number,
    field: string,
    value: string | number | boolean,
  ) => {
    const nextCurrent = currentJobForms.map((row, idx) =>
      idx === rowIndex ? patchEmploymentRow(row, field, value, false) : row,
    );
    const enterDateOverride =
      field === "start_date" || field === "end_date"
        ? buildEnterDatePatch(
            field === "start_date" ? "job_start_date" : "job_end_date",
            "current_employment",
            rowIndex,
            value,
          )
        : undefined;
    commitEmploymentForms(nextCurrent, previousJobForms, undefined, {
      enterDateOverride,
    });
  };

  const handlePreviousJobChange = (
    rowIndex: number,
    field: string,
    value: string | number | boolean,
  ) => {
    const nextPrevious = previousJobForms.map((row, idx) => {
      if (idx !== rowIndex) return row;
      const updated = patchEmploymentRow(row, field, value, true);
      if (
        (field === "start_date" || field === "end_date") &&
        isEndDateBeforeStartDate(updated.start_date, updated.end_date)
      ) {
        showError("Job End Date should be after Job Start Date");
        return patchEmploymentRow(updated, "end_date", "", true);
      }
      return updated;
    });
    const enterDateOverride =
      field === "start_date" || field === "end_date"
        ? buildEnterDatePatch(
            field === "start_date" ? "job_start_date" : "job_end_date",
            "previous_employment",
            rowIndex,
            value,
          )
        : undefined;
    commitEmploymentForms(currentJobForms, nextPrevious, undefined, {
      enterDateOverride,
    });
  };

  const handleCurrentJobBatchChange = (
    rowIndex: number,
    updates: Partial<EmploymentFormRow>,
  ) => {
    const nextCurrent = currentJobForms.map((row, idx) =>
      idx === rowIndex ? patchEmploymentRowFields(row, updates, false) : row,
    );
    commitEmploymentForms(nextCurrent, previousJobForms);
  };

  const handlePreviousJobBatchChange = (
    rowIndex: number,
    updates: Partial<EmploymentFormRow>,
  ) => {
    const nextPrevious = previousJobForms.map((row, idx) =>
      idx === rowIndex ? patchEmploymentRowFields(row, updates, true) : row,
    );
    commitEmploymentForms(currentJobForms, nextPrevious);
  };

  const handleAddCurrentJob = () => {
    commitEmploymentForms(
      [...currentJobForms, buildEmptyEmploymentRow()],
      previousJobForms,
    );
  };

  const handleRemoveCurrentJob = (rowIndex: number) => {
    const removed = currentJobForms[rowIndex];
    const nextDeleted = appendDeletedJobId(
      deletedJobIdsRef.current,
      removed?.job_id,
    );
    setDeletedJobIds(nextDeleted);
    const nextCurrent = currentJobForms.filter((_, idx) => idx !== rowIndex);
    setCurrentJobForms(nextCurrent);
    const previousWithGaps = mergePreviousJobGaps(nextCurrent, previousJobForms);
    setPreviousJobForms(previousWithGaps);
    emitFormChange(
      nextCurrent,
      previousWithGaps,
      otherIncomeForms,
      undefined,
      nextDeleted,
    );
  };

  const handleAddPreviousJob = () => {
    commitEmploymentForms(currentJobForms, [
      ...previousJobForms,
      buildEmptyEmploymentRow(),
    ]);
  };

  const handleRemovePreviousJob = (rowIndex: number) => {
    const removed = previousJobForms[rowIndex];
    const nextDeleted = appendDeletedJobId(
      deletedJobIdsRef.current,
      removed?.job_id,
    );
    setDeletedJobIds(nextDeleted);
    const nextPrevious = previousJobForms.filter((_, idx) => idx !== rowIndex);
    setPreviousJobForms(nextPrevious);
    emitFormChange(
      currentJobForms,
      nextPrevious,
      otherIncomeForms,
      undefined,
      nextDeleted,
    );
  };

  const openMakePreviousPopup = (rowIndex: number) => {
    setPendingMakePreviousEndDate("");
    setPendingMakePreviousIndex(rowIndex);
  };

  const resolveJobChangeError = (error: unknown): string =>
    (error as { message?: string })?.message || "Failed to update job.";

  const handleMakePrevious = async (rowIndex: number, endDate: string) => {
    const row = currentJobForms[rowIndex];
    if (!row) return;

    // Angular: local move mirrors reloaded state; saved jobs persist via /api/previous_application first.
    const applyLocalMove = () => {
      const updatedRow: EmploymentFormRow = {
        ...row,
        end_date: endDate,
        job_duration: deriveJobDurationMonths(row.start_date, endDate, {
          treatAsPresentJob: false,
        }),
      };
      const nextCurrent = currentJobForms.filter((_, idx) => idx !== rowIndex);
      const nextPrevious = [...previousJobForms, updatedRow];
      const nextEmploymentStatus = nextCurrent.some(isEmploymentRowMeaningful)
        ? "1"
        : "0";
      setHasPreviousEmployer(true);
      setEmploymentStatus(nextEmploymentStatus);
      commitEmploymentForms(nextCurrent, nextPrevious, nextEmploymentStatus);
    };

    if (applicantId && row.job_id) {
      try {
        await makeJobPreviousOrCurrent({
          applicantId,
          jobId: row.job_id,
          type: "previous",
          jobEndDate: endDate,
        });
        showSuccess("Mark as previous Job Successfully");
        applyLocalMove();
        if (onRefresh) await onRefresh();
      } catch (error: unknown) {
        showError(resolveJobChangeError(error));
      }
      return;
    }

    applyLocalMove();
  };

  const handleMakeCurrent = async (rowIndex: number) => {
    const row = previousJobForms[rowIndex];
    if (!row) return;

    const applyLocalMove = () => {
      const updatedRow: EmploymentFormRow = {
        ...row,
        end_date: "",
        job_duration:
          deriveJobDurationMonths(row.start_date, null, {
            treatAsPresentJob: true,
          }) || row.job_duration,
      };
      const nextPrevious = previousJobForms.filter((_, idx) => idx !== rowIndex);
      const currentWithoutPlaceholders = currentJobForms.filter(
        (currentRow) =>
          currentRow.job_id || isEmploymentRowMeaningful(currentRow),
      );
      const nextCurrent = [...currentWithoutPlaceholders, updatedRow];
      setEmploymentStatus("1");
      setHasPreviousEmployer(nextPrevious.some(isEmploymentRowMeaningful));
      commitEmploymentForms(nextCurrent, nextPrevious, "1");
    };

    if (applicantId && row.job_id) {
      try {
        await makeJobPreviousOrCurrent({
          applicantId,
          jobId: row.job_id,
          type: "current",
        });
        showSuccess("Mark as current Job Successfully");
        applyLocalMove();
        if (onRefresh) await onRefresh();
      } catch (error: unknown) {
        showError(resolveJobChangeError(error));
      }
      return;
    }

    applyLocalMove();
  };

  const handleEmploymentStatusChange = (status: string) => {
    setEmploymentStatus(status);
    // Angular checkEmployerGroup parity.
    const nextCurrent = status === "1" ? [buildEmptyEmploymentRow()] : [];
    setCurrentJobForms(nextCurrent);
    emitFormChange(nextCurrent, previousJobForms, otherIncomeForms, status);
  };

  const handlePreviousEmployerToggle = (value: boolean) => {
    setHasPreviousEmployer(value);
    if (value && previousJobForms.length === 0) {
      commitEmploymentForms(currentJobForms, [buildEmptyEmploymentRow()]);
      return;
    }
    if (!value) {
      const nextDeleted = appendDeletedJobIdsFromRows(
        deletedJobIdsRef.current,
        previousJobForms,
      );
      setDeletedJobIds(nextDeleted);
      setPreviousJobForms([]);
      emitFormChange(currentJobForms, [], otherIncomeForms, undefined, nextDeleted);
    }
  };

  const currentJobFormsRef = useRef(currentJobForms);
  currentJobFormsRef.current = currentJobForms;
  const previousJobFormsRef = useRef(previousJobForms);
  previousJobFormsRef.current = previousJobForms;

  // Angular translateContent: blur non-English fields → POST /api/translate; refs avoid clobbering edits.
  const translateAndPatchRow = async (
    rowIndex: number,
    field: string,
    value: string,
    isPrevious: boolean,
  ) => {
    const translated = await translateFieldOnBlur(field, value);
    if (!translated) return;
    if (isPrevious) {
      const nextPrevious = previousJobFormsRef.current.map((row, idx) =>
        idx === rowIndex ? patchEmploymentRow(row, field, translated, true) : row,
      );
      commitEmploymentForms(currentJobFormsRef.current, nextPrevious);
    } else {
      const nextCurrent = currentJobFormsRef.current.map((row, idx) =>
        idx === rowIndex ? patchEmploymentRow(row, field, translated, false) : row,
      );
      commitEmploymentForms(nextCurrent, previousJobFormsRef.current);
    }
  };

  const handleCurrentJobTranslate = (
    rowIndex: number,
    field: string,
    value: string,
  ) => void translateAndPatchRow(rowIndex, field, value, false);

  const handlePreviousJobTranslate = (
    rowIndex: number,
    field: string,
    value: string,
  ) => void translateAndPatchRow(rowIndex, field, value, true);

  const propertyAddressForDistance = useMemo(
    () => buildPropertyAddressForDistance(applicationData, applicationAllData),
    [applicationData, applicationAllData],
  );

  const updateCurrentJobDistance = useCallback(
    async (rowIndex: number, form: EmploymentFormRow) => {
      const jobAddress = buildEmployerAddressForDistance(form);
      if (!propertyAddressForDistance || !jobAddress) return;
      try {
        const distance = await calculatePropertyToEmployerDistance(
          propertyAddressForDistance,
          jobAddress,
        );
        const nextCurrent = currentJobFormsRef.current.map((row, idx) =>
          idx === rowIndex
            ? patchEmploymentRowFields(
                row,
                { ...form, job_distance: distance },
                false,
              )
            : row,
        );
        commitEmploymentForms(nextCurrent, previousJobFormsRef.current);
      } catch {
        // ZERO_RESULTS — Angular silently ignores.
      }
    },
    [commitEmploymentForms, propertyAddressForDistance],
  );

  const handleCurrentJobAddressBlur = useCallback(
    async (rowIndex: number) => {
      const form = currentJobFormsRef.current[rowIndex];
      if (!form) return;
      await updateCurrentJobDistance(rowIndex, form);
    },
    [updateCurrentJobDistance],
  );

  const handleCurrentJobAddressResolved = useCallback(
    async (rowIndex: number, updates: Partial<EmploymentFormRow>) => {
      const currentRow = currentJobFormsRef.current[rowIndex];
      if (!currentRow) return;
      await updateCurrentJobDistance(rowIndex, { ...currentRow, ...updates });
    },
    [updateCurrentJobDistance],
  );

  const autoDistanceCalculationKeysRef = useRef<Record<number, string>>({});
  useEffect(() => {
    if (!propertyAddressForDistance) return;
    currentJobForms.forEach((row, rowIndex) => {
      const jobAddress = buildEmployerAddressForDistance(row);
      if (!jobAddress || parseJobDistance(row.job_distance) > 0) return;
      const key = `${propertyAddressForDistance}|${jobAddress}`;
      if (autoDistanceCalculationKeysRef.current[rowIndex] === key) return;
      autoDistanceCalculationKeysRef.current[rowIndex] = key;
      void updateCurrentJobDistance(rowIndex, row);
    });
  }, [currentJobForms, propertyAddressForDistance, updateCurrentJobDistance]);

  const incomeTabValidation = useMemo(() => {
    const other_source: OtherSourceInput[] = otherIncomeForms.map((form) => {
      const enriched = enrichOtherIncomeRow(form);
      return {
        job_id: String(enriched.job_id ?? ""),
        source_name: String(enriched.source_name ?? ""),
        income: String(enriched.income ?? ""),
        income_period: String(enriched.income_period ?? ""),
        job_start_date: String(enriched.job_start_date ?? ""),
        job_end_date: String(enriched.job_end_date ?? ""),
        present_job: enriched.present_job ? "1" : "0",
        job_duration: enriched.job_duration,
      };
    });
    return validateIncomeEmploymentTab({
      employment_status:
        employmentStatus === "0" || employmentStatus === "1"
          ? employmentStatus
          : "",
      current_employment: [],
      previous_employment: [],
      other_source,
    });
  }, [employmentStatus, otherIncomeForms]);

  const otherIncomeFieldMsg = (
    index: number,
    field: OtherSourceValidatedField,
  ): string | undefined => {
    const msg = incomeTabValidation.otherSourceFieldErrors?.[index]?.[field];
    if (!msg) return undefined;
    if (
      exposeIncomeInlineValidation ||
      submitAttempted ||
      otherIncomeTouched[`${index}.${field}`]
    ) {
      return msg;
    }
    return undefined;
  };

  return {
    currentJobs,
    previousJobs,
    otherIncomeData,
    currentJobForms,
    previousJobForms,
    otherIncomeForms,
    employmentStatus,
    hasPreviousEmployer,
    submitAttempted,
    statesList,
    propertyAddressForDistance,
    pendingMakePreviousIndex,
    setPendingMakePreviousIndex,
    pendingMakePreviousEndDate,
    setPendingMakePreviousEndDate,
    makePreviousConfirm,
    setMakePreviousConfirm,
    makeCurrentConfirm,
    setMakeCurrentConfirm,
    handleOtherIncomeChange,
    handleAddOtherIncome,
    handleRemoveOtherIncome,
    handleCurrentJobChange,
    handlePreviousJobChange,
    handleCurrentJobTranslate,
    handlePreviousJobTranslate,
    handleCurrentJobBatchChange,
    handlePreviousJobBatchChange,
    handleAddCurrentJob,
    handleRemoveCurrentJob,
    handleAddPreviousJob,
    handleRemovePreviousJob,
    openMakePreviousPopup,
    handleMakePrevious,
    handleMakeCurrent,
    handleEmploymentStatusChange,
    handlePreviousEmployerToggle,
    handleCurrentJobAddressBlur,
    handleCurrentJobAddressResolved,
    otherIncomeFieldMsg,
  };
}
